import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TIME_FILTER } from 'constants/chart';
import { Chart } from 'pages/Pae/components/DailyTracking/components/EndCard/components/DailyTrackingChart/components/Chart';
import { FilterButtons } from 'pages/Pae/components/DailyTracking/components/EndCard/components/DailyTrackingChart/components/FilterButtons';
import type { TimeFilter, ChartDataPoint } from 'types/chart';
import { type SurveyChartProps } from './types';
import { useChartData } from './useChartData';

export const SurveyChart = ({
  surveyType,
  surveyId,
  title,
  showFilters = true,
}: SurveyChartProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>(TIME_FILTER.LAST_7_DAYS);
  const [selectedWeek, setSelectedWeek] = useState<ChartDataPoint | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<{ year: number; month: number } | null>(null);
  const { chartData, isLoading, error } = useChartData({
    surveyType,
    surveyId,
    timeFilter,
    selectedWeek,
    selectedMonth,
  });
  const hasError = Boolean(error);
  const hasNoData = !chartData.length;
  const showEmptyState = hasError || hasNoData;

  return (
    <div className="w-full">
      {(title || showFilters) && (
        <div
          className={cn(
            'flex flex-col sm:flex-row sm:items-center gap-4 mb-4',
            !title && 'sm:justify-end'
          )}
        >
          {title && (
            <h3 className="text-lg font-semibold text-center sm:text-left text-gray-800">
              {title}
            </h3>
          )}
          {showFilters && (
            <div className={cn(!title && 'w-full sm:w-auto flex justify-end')}>
              <FilterButtons
                timeFilter={timeFilter}
                selectedWeek={selectedWeek}
                selectedMonth={selectedMonth}
                onTimeFilterChange={setTimeFilter}
                onSelectedWeekChange={setSelectedWeek}
                onSelectedMonthChange={setSelectedMonth}
              />
            </div>
          )}
        </div>
      )}
      {isLoading ? (
        <LoadingScreen />
      ) : showEmptyState ? (
        <div className="flex flex-col items-center justify-center h-64 gap-2">
          <p className="text-gray-600 text-center">{t('DailyTracking.Chart.empty')}</p>
        </div>
      ) : (
        <Chart chartData={chartData} onWeekClick={setSelectedWeek} />
      )}
    </div>
  );
};
